// client/src/components/reports/ShareModal.jsx
//
// Read-only share links for a saved report or board report. Anyone with the
// link can open it without signing in; personal details are always masked.
// The link is shown ONCE, when it is made (only a hash is stored), so the
// list shows a hint, status, expiry and view count, and lets you turn a link
// off. Board reports share with the inputs chosen on screen.
//
// API: /api/reports/:id/shares (server/routes/reportRoutes.js).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCopy } from 'react-icons/fa';
import { listShares, createShare, revokeShare, errorText } from '../../services/reportsApi';

const EXPIRY = [1, 7, 30, 90, 365];

function when(value) {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T') + (String(value).includes('T') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

export default function ShareModal({ reportId, reportName, inputs, onClose }) {
  const { t } = useTranslation();
  const [links, setLinks] = useState(null);
  const [expires, setExpires] = useState('30');
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const dialog = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await listShares(reportId);
      setLinks(r.shares || []);
    } catch (err) {
      setError(errorText(err, t('reports.share.loadError', 'Could not load share links.')));
      setLinks([]);
    }
  }, [reportId, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    dialog.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const make = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      const r = await createShare(reportId, {
        expiresInDays: expires === 'never' ? null : Number(expires),
        label: label.trim() || null,
        ...(inputs ? { inputs } : {}),
      });
      setCreated(r);
      setLabel('');
      await load();
    } catch (err) {
      setError(errorText(err, t('reports.share.createError', 'Could not create the link.')));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const turnOff = async (s) => {
    try {
      await revokeShare(reportId, s.id);
      await load();
    } catch (err) {
      setError(errorText(err, t('reports.share.revokeError', 'Could not turn off the link.')));
    }
  };

  const statusLabel = {
    active: t('reports.share.active', 'Active'),
    expired: t('reports.share.expired', 'Expired'),
    revoked: t('reports.share.revoked', 'Turned off'),
  };

  return (
    <div className="rp-overlay" role="presentation" onClick={onClose}>
      <div
        className="rp-modal surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rp-share-title"
        tabIndex={-1}
        ref={dialog}
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        <h3 id="rp-share-title">{t('reports.share.title', 'Share “{{name}}”', { name: reportName })}</h3>
        <p className="surface-muted perf-small" style={{ margin: 0 }}>
          {t('reports.share.intro', 'Anyone with the link can view this report without signing in — for example a board member. Personal details such as emails and phone numbers are always hidden. The numbers update each time the link is opened.')}
        </p>

        {created && (
          <div className="note info rp-share-created" role="status">
            <p style={{ margin: '0 0 6px' }}>{t('reports.share.createdNote', 'Copy the link now — for security it is shown only once.')}</p>
            <div className="rp-share-url">
              <input type="text" readOnly value={created.url} aria-label={t('reports.share.linkLabel', 'Share link')} onFocus={(e) => e.target.select()} />
              <button type="button" className="lm-btn" onClick={copy}>
                <FaCopy aria-hidden="true" /> {copied ? t('reports.share.copied', 'Copied') : t('reports.share.copy', 'Copy')}
              </button>
            </div>
          </div>
        )}

        {links && links.length > 0 && (
          <ul className="rp-sched-list" aria-label={t('reports.share.listLabel', 'Share links')}>
            {links.map((s) => (
              <li key={s.id} className="rp-run">
                <span>
                  <strong>{s.label || `…${s.tokenHint}`}</strong>
                  {' · '}{statusLabel[s.status]}
                  {s.expiresAt ? ` · ${t('reports.share.expiresOn', 'expires {{date}}', { date: when(s.expiresAt) })}` : ` · ${t('reports.share.noExpiry', 'no expiry')}`}
                  {' · '}{t('reports.share.views', '{{count}} view(s)', { count: s.viewCount })}
                </span>
                {s.status === 'active' && (
                  <button type="button" className="rp-icon-btn" onClick={() => turnOff(s)}>
                    {t('reports.share.turnOff', 'Turn off')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <form className="fo-form-grid" onSubmit={make}>
          <label className="fo-field" htmlFor="rp-share-label">
            {t('reports.share.labelField', 'Name (optional, e.g. “Board — October”)')}
            <input id="rp-share-label" type="text" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label className="fo-field" htmlFor="rp-share-expiry">
            {t('reports.share.expiry', 'Link works for')}
            <select id="rp-share-expiry" value={expires} onChange={(e) => setExpires(e.target.value)}>
              {EXPIRY.map((d) => <option key={d} value={String(d)}>{t('reports.share.days', '{{count}} day(s)', { count: d })}</option>)}
              <option value="never">{t('reports.share.never', 'Until I turn it off')}</option>
            </select>
          </label>
          {error && <div className="note error" role="alert" style={{ gridColumn: '1 / -1' }}>{error}</div>}
          <div className="rp-actions" style={{ gridColumn: '1 / -1', justifyContent: 'flex-end' }}>
            <button type="button" className="lm-btn secondary" onClick={onClose}>{t('reports.share.close', 'Close')}</button>
            <button type="submit" className="lm-btn" disabled={busy}>{t('reports.share.create', 'Create link')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
